import * as React from 'react';
import { Link2, ShieldCheck } from 'lucide-react';
import { useToast, workspaceFieldClass } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { draftApi, useFrameworkVersionsQuery } from '@/lib/api/discovery-frameworks';
import {
  LINK_TTL_MAX_DAYS,
  LINK_TTL_MIN_DAYS,
  useDiscoverySettingsQuery,
  useUpdateLinkLifetimeMutation,
} from '@/lib/api/discovery-settings';
import { cn } from '@/lib/utils/cn';
import type { EditorCtx } from './editor';
import { SOURCES_MAX, SOURCES_MIN, formatDate, messageOf } from './framework-logic';
import { CautionNote } from './parts';
import { TAB_SCROLL } from './card-tabs';

/**
 * The Rules and History tabs (prototype `#frameworks`, `.card-flat.rule`
 * and `.trail-list`).
 *
 * Rules: the sources each area needs is the one framework rule, 1 to 4,
 * changed in the draft; at 1 the tab says corroboration is turned off
 * (Decision 54.6). The secure-link lifetime is a firm-wide setting a firm
 * admin changes here at once, outside any draft (Decision 54.26). The
 * fixed rules are shown and cannot be changed.
 */

const CARD = 'flex items-center gap-4 rounded-card border border-ink-100 bg-paper-0 px-5 py-4';
const STEP =
  'inline-flex h-7 w-7 items-center justify-center rounded-chip border border-ink-200 bg-paper-0 text-body font-medium text-ink-700 hover:border-ink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-40';

function Rule({
  title,
  children,
  control,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  control?: React.ReactNode;
}) {
  return (
    <div className={CARD}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-ink-900">{title}</div>
        <div className="text-meta text-ink-500">{children}</div>
      </div>
      {control}
    </div>
  );
}

export function RulesTab({ ctx }: { ctx: EditorCtx }) {
  const { content, editing, busy, write } = ctx;
  const sources = content.version?.sources_required ?? 2;
  const setSources = (n: number) =>
    void write((id, base) => draftApi.setRules(id, base, n), { what: 'The rule' });

  return (
    <div className={TAB_SCROLL}>
      <div className="flex max-w-[820px] flex-col gap-3">
        <Rule
          title="Independent sources each area needs"
          control={
            <div className="flex flex-none items-center gap-2.5 text-h2 text-ink-900">
              {editing ? (
                <button
                  type="button"
                  aria-label="Fewer sources"
                  className={STEP}
                  disabled={busy || sources <= SOURCES_MIN}
                  onClick={() => setSources(sources - 1)}
                >
                  −
                </button>
              ) : null}
              <b className="font-semibold tabular-nums" aria-label={`${sources} sources`}>
                {sources}
              </b>
              {editing ? (
                <button
                  type="button"
                  aria-label="More sources"
                  className={STEP}
                  disabled={busy || sources >= SOURCES_MAX}
                  onClick={() => setSources(sources + 1)}
                >
                  +
                </button>
              ) : null}
            </div>
          }
        >
          A source is a person (through an interview, questionnaire or follow-up) or a document. One
          person counts once, however much they said.
        </Rule>
        {sources === 1 ? (
          <CautionNote>
            <b className="font-semibold">Corroboration is turned off for this framework.</b> One
            person or one document covers an area. The publish dialog and every engagement on this
            version say so. Set it from 1 to 4; 2 is the default.
          </CautionNote>
        ) : null}
        <LinkLifetimeRule />
        <Rule
          title="AI-proposed evidence counts only after a consultant confirms it"
          control={
            <ShieldCheck className="h-[18px] w-[18px] flex-none text-ink-300" aria-hidden="true" />
          }
        >
          Always on. Not a setting.
        </Rule>
        <Rule
          title="A waiver of sign-off needs a written reason"
          control={
            <ShieldCheck className="h-[18px] w-[18px] flex-none text-ink-300" aria-hidden="true" />
          }
        >
          At least 20 characters, recorded with who waived and when, and printed on the exported
          packet.
        </Rule>
        <Rule title="Follow-up questions">
          Between 1 and 8 questions, sent to one named person.
        </Rule>
      </div>
    </div>
  );
}

const DAYS = Array.from(
  { length: LINK_TTL_MAX_DAYS - LINK_TTL_MIN_DAYS + 1 },
  (_, i) => i + LINK_TTL_MIN_DAYS,
);

function LinkLifetimeRule() {
  const { toast } = useToast();
  const settings = useDiscoverySettingsQuery();
  const update = useUpdateLinkLifetimeMutation();
  const days = settings.data?.link_ttl_days;
  return (
    <Rule
      title={
        days != null ? `Secure links last ${days} ${days === 1 ? 'day' : 'days'}` : 'Secure links'
      }
      control={
        settings.data?.can_edit ? (
          <select
            aria-label="Secure link lifetime"
            value={days}
            disabled={update.isPending}
            onChange={(e) => {
              const next = Number(e.target.value);
              update.mutate(next, {
                onSuccess: () =>
                  toast({
                    message: `Secure links now last ${next} ${next === 1 ? 'day' : 'days'}. Links already sent keep their expiry.`,
                  }),
                onError: (err) =>
                  toast({
                    message: `The link lifetime was not changed: ${messageOf(err, 'the change was refused.')}`,
                    tone: 'critical',
                  }),
              });
            }}
            className={cn(workspaceFieldClass, 'h-9 w-[110px] flex-none pr-8')}
          >
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d} {d === 1 ? 'day' : 'days'}
              </option>
            ))}
          </select>
        ) : (
          <Link2 className="h-[18px] w-[18px] flex-none text-ink-300" aria-hidden="true" />
        )
      }
    >
      For questionnaires, follow-ups, map validation and sign-off. A firm-wide default that a firm
      admin changes for every framework; it applies to links sent from then on. The engagement lead
      can extend a link.
    </Rule>
  );
}

export function HistoryTab({ frameworkId }: { frameworkId: string }) {
  const query = useFrameworkVersionsQuery(frameworkId);
  return (
    <div className={TAB_SCROLL}>
      <div className="max-w-[820px] rounded-card border border-ink-100 bg-paper-0 px-5 py-4">
        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorMessage error={query.error} />
        ) : query.data.versions.length === 0 ? (
          <p className="text-body-sm text-ink-500">Not published yet.</p>
        ) : (
          <ul
            aria-label="Published versions"
            className="relative before:absolute before:bottom-1 before:left-[5px] before:top-1 before:w-px before:bg-ink-200"
          >
            {query.data.versions.map((v) => (
              <li
                key={v.version}
                className="relative grid grid-cols-[180px_1fr] items-start gap-4 py-1.5 pl-[22px]"
              >
                <span className="absolute left-0 top-3 z-[1] h-[11px] w-[11px] rounded-full border-2 border-posture-healthy bg-paper-0" />
                <span className="text-meta text-ink-500">
                  v{v.version} · {formatDate(v.published_at)}
                </span>
                <span className="text-body-sm text-ink-700">
                  <strong className="font-semibold text-ink-900">
                    {v.published_by?.name ?? 'Caliber'}
                  </strong>
                  : {v.change_note ?? 'No change note'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
